import { useFormatter, useTranslations } from "use-intl";
import { useState } from "react";
import { Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { finishSession, type FinishResult } from "./actions";
import { Stepper } from "@/components/app/stepper";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import type { LoggedSet } from "./model";
import * as outbox from "@/lib/outbox";
import { cn } from "@/lib/utils";

const FEELINGS = ["😫", "😕", "🙂", "😃", "🤩"];

export function FinishStep({
  sessionId,
  startedAt,
  logged,
  isCardio,
  loggedLater,
  onFinished,
}: {
  sessionId: string;
  startedAt: string;
  logged: LoggedSet[];
  isCardio: boolean;
  /** Set for workouts entered afterwards: the duration is asked for instead of measured. */
  loggedLater: { defaultMinutes: number } | null;
  onFinished: (r: FinishResult) => void;
}) {
  const t = useTranslations("finish");
  const tc = useTranslations("common");
  const format = useFormatter();
  const [feeling, setFeeling] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const working = logged.filter((s) => !s.isWarmup);
  const [now] = useState(() => Date.now());
  const measured = Math.max(1, Math.round((now - new Date(startedAt).getTime()) / 60000));
  const [enteredMinutes, setEnteredMinutes] = useState<number | null>(loggedLater?.defaultMinutes ?? null);
  const minutes = loggedLater ? (enteredMinutes ?? 0) : measured;
  const volume = working.reduce((sum, s) => sum + (s.weight ?? 0) * (s.reps ?? 0), 0);

  async function save() {
    setSaving(true);
    // Every set must be on the server before the workout can be scored.
    if (!(await outbox.flush()) || outbox.pendingCount(sessionId) > 0) {
      toast.error(t("offline"));
      setSaving(false);
      return;
    }
    try {
      onFinished(
        await finishSession({
          sessionId,
          feeling,
          notes: notes.trim() || null,
          ...(loggedLater && enteredMinutes ? { durationMin: enteredMinutes } : {}),
        }),
      );
      outbox.clearSession(sessionId);
    } catch {
      toast.error(tc("error"));
      setSaving(false);
    }
  }

  return (
    <div className="pt-6">
      <div className="flex size-14 items-center justify-center rounded-2xl bg-xp/15 text-xp">
        <Trophy className="size-7" />
      </div>
      <h1 className="mt-4 text-3xl font-black tracking-tight">{t("title")}</h1>

      <div className="mt-6 grid grid-cols-3 gap-2">
        <Stat label={t("duration")} value={tc("minutes", { count: minutes })} />
        <Stat label={t("sets")} value={String(working.length)} />
        <Stat
          label={t("volume")}
          value={isCardio ? "–" : `${format.number(volume, { maximumFractionDigits: 0 })} kg`}
        />
      </div>

      {loggedLater && logged.length > 0 && (
        <Stepper
          className="mt-4"
          label={t("durationMinutes")}
          value={enteredMinutes}
          step={5}
          min={1}
          max={600}
          onChange={setEnteredMinutes}
        />
      )}

      {logged.length === 0 ? (
        <p className="mt-6 rounded-2xl bg-muted p-4 text-sm">{t("empty")}</p>
      ) : (
        <>
          <p className="mt-8 font-semibold">{t("feeling")}</p>
          <div className="mt-3 grid grid-cols-5 gap-2">
            {FEELINGS.map((emoji, i) => (
              <button
                key={emoji}
                type="button"
                onClick={() => setFeeling(i + 1)}
                className={cn(
                  "flex flex-col items-center gap-1 rounded-2xl border p-2 transition",
                  feeling === i + 1 ? "border-primary bg-primary/10 scale-105" : "opacity-80",
                )}
              >
                <span className="text-3xl">{emoji}</span>
                <span className="text-[11px] text-muted-foreground">
                  {t(`feelings.${i + 1}` as "feelings.1")}
                </span>
              </button>
            ))}
          </div>
          <p className="mt-6 font-semibold">{t("notes")}</p>
          <Textarea
            className="mt-2 min-h-20 text-base"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("notesPlaceholder")}
          />
          <Button
            size="lg"
            className="mt-6 h-14 w-full rounded-2xl text-base"
            disabled={saving || (loggedLater != null && !enteredMinutes)}
            onClick={save}
          >
            {saving && <Loader2 className="animate-spin" />}
            {t("save")}
          </Button>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border bg-card p-3 text-center">
      <p className="text-lg font-bold tabular-nums">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}
